import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { FormResponses } from '@/types/solidarity';
import { getQuestionSteps } from '@/utils/questionSteps';
import { QuestionRenderer } from '@/components/QuestionRenderer';
import { ResultDisplay } from '@/components/ResultDisplay';
import { calculateSolidarityIndex } from '@/utils/calculations';

export const SolidarityCalculator: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(-1); // Start at -1 for welcome screen
  const [responses, setResponses] = useState<FormResponses>({
    volunteering: {},
    donations: {},
    attitudes: {}
  });
  const [isComplete, setIsComplete] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [allQuestionSteps] = useState(() => getQuestionSteps()); // Fixed set of all questions

  // Calculate visible steps but don't change total count during session
  const visibleSteps = useMemo(() => {
    return allQuestionSteps.filter(step => step.shouldShow(responses));
  }, [allQuestionSteps, responses]);

  // Use visible steps count for progress calculation
  const totalQuestions = visibleSteps.length;
  const currentQuestion = currentStep >= 0 ? visibleSteps[currentStep] : null;
  const progress = currentStep >= 0 ? Math.round(((currentStep + 1) / totalQuestions) * 100) : 0;

  const updateResponse = useCallback((section: keyof FormResponses, key: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  }, []);

  const handleNext = async () => {
    if (currentStep === -1) {
      // Start questionnaire
      setCurrentStep(0);
    } else if (currentStep < visibleSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    } else {
      // Complete the questionnaire
      setIsProcessing(true);
      // Add a small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsComplete(true);
      setIsProcessing(false);
    }
  };

  const handleStart = () => {
    setCurrentStep(0);
  };

  const isCurrentQuestionAnswered = () => {
    if (!currentQuestion) return false;
    const sectionResponses = responses[currentQuestion.section];
    // Handle Q04 multi-select question
    if (currentQuestion.questionKey === 'Q04') {
      const hasSelection = responses.volunteering.Q04_A || 
                          responses.volunteering.Q04_B || 
                          responses.volunteering.Q04_C || 
                          responses.volunteering.Q04_D;
      return hasSelection;
    }
    // Handle D17 multi-select question
    if (currentQuestion.questionKey === 'D17') {
      const hasSelection = responses.donations.D17_A || 
                          responses.donations.D17_B || 
                          responses.donations.D17_C || 
                          responses.donations.D17_D || 
                          responses.donations.D17_E || 
                          responses.donations.D17_F;
      return hasSelection;
    }
    // Handle regular single-value questions
    const answer = sectionResponses[currentQuestion.questionKey as keyof typeof sectionResponses];
    return answer !== undefined && answer !== null;
  };

  const getSectionInfo = () => {
    if (!currentQuestion) return { name: '', color: '' };
    
    switch (currentQuestion.section) {
      case 'volunteering':
        return { name: 'Dobrovolnictví', color: 'text-green-600' };
      case 'donations':
        return { name: 'Dárcovství', color: 'text-blue-600' };
      case 'attitudes':
        return { name: 'Postoje', color: 'text-purple-600' };
      default:
        return { name: '', color: '' };
    }
  };

  const sectionInfo = getSectionInfo();

  if (isComplete) {
    const result = calculateSolidarityIndex(responses);
    return <ResultDisplay result={result} />;
  }

  // Welcome screen
  if (currentStep === -1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-2xl mx-auto pt-4 sm:pt-8">
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-6 px-4 sm:px-6">
              <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">
                Kalkulátor Indexu Solidarity
              </CardTitle>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Zjistěte svůj osobní index solidarity pomocí krátkého dotazníku. 
                Odpovězte na otázky týkající se dobrovolnictví, dárcovství a postojů 
                k solidaritě ve společnosti.
              </p>
            </CardHeader>

            <CardContent className="space-y-6 px-4 sm:px-6">
              <div className="bg-blue-50 rounded-lg p-4 sm:p-6">
                <h3 className="font-semibold text-slate-800 mb-3">Co vás čeká:</h3>
                <ul className="space-y-2 text-slate-600 text-sm sm:text-base">
                  <li>• Otázky o dobrovolnické činnosti</li>
                  <li>• Otázky o finančním a nefinančním dárcovství</li>
                  <li>• Otázky o postojích k solidaritě</li>
                  <li>• Výsledek s porovnáním v české populaci</li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-700 text-sm">
                  <strong>Doba vyplnění:</strong> přibližně 3-5 minut
                  <br />
                  <strong>Upozornění:</strong> Ve formuláři nelze jít zpět na předchozí otázky
                </p>
              </div>
              <div className="text-center pt-4">
                <Button
                  onClick={handleStart}
                  className="w-full sm:w-auto px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-lg"
                >
                  Začít dotazník
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-2xl mx-auto pt-4 sm:pt-8">
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-6 px-4 sm:px-6">
            <CardTitle className="text-xl sm:text-2xl font-bold text-slate-800">
              Kalkulátor Indexu Solidarity
            </CardTitle>
            <p className="text-slate-600 mt-2 text-sm sm:text-base">
              Odpovězte na otázky a zjistěte svůj osobní index solidarity
            </p>
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-500">
                  Otázka {currentStep + 1} z {totalQuestions}
                </p>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${sectionInfo.color === 'text-green-600' ? 'bg-green-500' : sectionInfo.color === 'text-blue-600' ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
                  <p className={`text-sm font-medium ${sectionInfo.color}`}>
                    {sectionInfo.name}
                  </p>
                </div>
              </div>
              <Progress value={progress} className="w-full h-2" />
              <div className="text-center mt-2">
                <p className="text-xs text-slate-400">
                  {progress}% dokončeno
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pb-8 px-4 sm:px-6">
            {currentQuestion ? (
              <div key={currentQuestion.id}>
                <QuestionRenderer
                  question={currentQuestion}
                  responses={responses}
                  onUpdate={updateResponse}
                />
              </div>
            ) : (
              <div className="text-center text-slate-500">
                Načítám otázku...
              </div>
            )}
            <div className="flex justify-end mt-8">
              <Button
                onClick={handleNext}
                disabled={!isCurrentQuestionAnswered() || isProcessing}
                className="w-full sm:w-auto px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-300 disabled:text-slate-500 transition-colors"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Zpracovávám...
                  </>
                ) : (
                  currentStep < visibleSteps.length - 1 ? 'Pokračovat' : 'Dokončit'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};